import { inforBlackIcon } from '@/shared/ui/assets';

import { STUDY_TYPES, FOLLOW_ON_STUDY_TYPE } from '@/entities/study';
import { getStudyDuration } from '@/shared/lib';
import _ from 'lodash';

/**
 * Resets a study's detection settings when its type crosses between the MCT/cardiac-event family and the Holter family. Moving to a Holter type returns the facility pre/post event times with bradycardia 1, tachycardia 300, pause level 2.5, both detections off and diagnostic lead 2; moving back to a normal type returns the facility study setting values with hardcoded fallbacks; any other transition returns studySetting untouched.
 *
 * @param facilityStudySetting - The facility-level study setting supplying default thresholds and times.
 * @param studySetting - The study's current setting, returned when the type change needs no reset.
 * @param oldStudyType - The study type before the change.
 * @param newStudyType - The study type being applied.
 * @returns The study setting to persist for the new study type.
 */
const generateDefaultStudySetting = (
  facilityStudySetting: any,
  studySetting: any,
  oldStudyType: string,
  newStudyType: string,
) => {
  const normalStudyTypeArray = [STUDY_TYPES.MCT, STUDY_TYPES.CARDIAC_EVENT, STUDY_TYPES.MCT_PEEK];
  const holterStudyTypeArray = [
    STUDY_TYPES.HOLTER,
    STUDY_TYPES.EXTENDED_HOLTER,
    FOLLOW_ON_STUDY_TYPE,
  ];

  if (
    _.includes(normalStudyTypeArray, oldStudyType) &&
    _.includes(holterStudyTypeArray, newStudyType)
  ) {
    return {
      preEventTime: facilityStudySetting ? facilityStudySetting.preEventTime : 30,
      postEventTime: facilityStudySetting ? facilityStudySetting.postEventTime : 30,
      bradycardiaThreshold: 1,
      tachycardiaThreshold: 300,
      pauseLevel: 2.5,
      pauseDetection: false,
      afibDetection: false,
      diagnosticLead: 2,
    };
  }
  if (
    _.includes(holterStudyTypeArray, oldStudyType) &&
    _.includes(normalStudyTypeArray, newStudyType)
  ) {
    return {
      preEventTime: facilityStudySetting ? facilityStudySetting.preEventTime : 30,
      postEventTime: facilityStudySetting ? facilityStudySetting.postEventTime : 30,
      bradycardiaThreshold: facilityStudySetting ? facilityStudySetting.bradycardiaThreshold : 60,
      tachycardiaThreshold: facilityStudySetting ? facilityStudySetting.tachycardiaThreshold : 120,
      pauseLevel: facilityStudySetting ? facilityStudySetting.pauseLevel : 2.5,
      pauseDetection: facilityStudySetting ? !!facilityStudySetting.pauseDetection : true,
      afibDetection: facilityStudySetting ? !!facilityStudySetting.afibDetection : false,
      diagnosticLead: facilityStudySetting ? facilityStudySetting.diagnosticLead : 2,
    };
  }

  return studySetting;
};

/**
 * Decides whether a study duration should be presented in days or hours. Uses duration when it is non-zero and initDuration otherwise, returning 'day' when the value is a whole multiple of 1440 minutes and 'hour' when it is not.
 *
 * @param duration - The edited duration in minutes, preferred when set.
 * @param initDuration - The study's original duration in minutes, used as fallback.
 * @returns The unit the duration should be displayed in.
 */
const getUnitTime = (duration: number, initDuration: number) => {
  if (duration) {
    return duration % 1440 === 0 ? 'day' : 'hour';
  }

  return initDuration % 1440 === 0 ? 'day' : 'hour';
};

/**
 * Converts a study duration in minutes into the unit chosen by getUnitTime. Divides by 1440 for days or 60 for hours, preferring the explicit duration, falling back to getStudyDuration over start and stop when a stop time is given, and to initDuration otherwise.
 *
 * @param duration - The edited duration in minutes, preferred when set.
 * @param initDuration - The study's original duration in minutes, used as last fallback.
 * @param start - Study start timestamp used when the duration is computed from the range.
 * @param stop - Study stop timestamp; its presence selects the computed-range branch.
 * @returns The duration expressed in days or hours.
 */
const getTimeDuration = (duration: number, initDuration: number, start?: string, stop?: string) => {
  const isUnitDays = getUnitTime(duration, initDuration) === 'day';

  if (duration) {
    return (duration || 0) / (isUnitDays ? 1440 : 60);
  }

  if (stop) {
    return getStudyDuration(start ?? '', stop) / (isUnitDays ? 1440 : 60);
  }

  return initDuration / (isUnitDays ? 1440 : 60);
};

/**
 * Explains why a study detail field cannot be edited in the edit study details form.
 * Switches on the field type to pick a functional-testing message, or for study type and duration
 * a completed-study message, and renders it with an info icon when one applies.
 *
 * @param props - Field type plus the isCompletedStudy and isTestStudy flags.
 * @returns The disabled-reason notice, or null when the field is editable.
 */
const getDisabledText = ({
  type,
  isCompletedStudy = false,
  isTestStudy = false,
}: {
  type: string;
  isCompletedStudy: boolean;
  isTestStudy: boolean;
}) => {
  let text = null;
  switch (type) {
    case 'studyType': {
      if (isTestStudy) {
        text = 'Cannot change study type of a functional testing study.';
        break;
      }
      // if (isAbortedStudy) {
      //   text = 'Cannot edit study type of aborted study.';
      //   break;
      // }
      if (isCompletedStudy) {
        text = 'Cannot change study type when the study is completed.';
        break;
      }
      break;
    }
    case 'duration': {
      if (isTestStudy) {
        text = 'Cannot change study duration of a functional testing study.';
        break;
      }
      // if (isAbortedStudy) {
      //   text = 'Cannot edit study duration of aborted study.';
      //   break;
      // }
      if (isCompletedStudy) {
        text = 'Cannot change study duration when the study is completed.';
        break;
      }
      break;
    }
    case 'followOnStudyStudyType': {
      if (isTestStudy) {
        text = 'Cannot change follow-on study type of a functional testing study.';
        break;
      }
      break;
    }
    case 'followOnStudyDuration': {
      if (isTestStudy) {
        text = 'Cannot change follow-on study duration of a functional testing study.';
        break;
      }
      break;
    }
    case 'reportDueDate': {
      if (isTestStudy) {
        text = 'Cannot change patient return visit of a functional testing study.';
        break;
      }
      break;
    }
    default: {
      break;
    }
  }

  if (text) {
    return (
      <div className="abort-input">
        <img src={inforBlackIcon} alt="inforBlackIcon" />
        {text}
      </div>
    );
  }

  return null;
};

export { generateDefaultStudySetting, getDisabledText, getTimeDuration, getUnitTime };
